import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BatteryCharging, CalendarRange, CheckCircle2, LucideAngularModule, RefreshCcw } from 'lucide-angular';
import { catchError, forkJoin, map, of } from 'rxjs';
import { BatteryConfiguration, BessForecastAsset, BessForecastBlock, BessForecastResponse, BessSchedule, ForecastPriceSnapshot, ForecastPriceSnapshotDetail } from '../../../core/models/domain.models';
import { ToastService } from '../../../shared/ui/toast.service';
import { BatteryConfigurationsService } from '../services/battery-configurations.service';
import { BessOperationsService } from '../services/bess-operations.service';

type BidProduct = 'DAM' | 'RTM';

type BidSuggestion = {
  block: number;
  blockLabel: string;
  marketPrice: number;
  action: 'charge' | 'discharge' | 'hold';
  bidMw: number;
  reason: string;
  eligible: boolean;
  selected: boolean;
  existingScheduleId: string | null;
  existingStatus: string | null;
};

type PlantBidPanel = {
  asset: BatteryConfiguration;
  suggestions: BidSuggestion[];
  summary: {
    totalEligible: number;
    chargeCount: number;
    dischargeCount: number;
    selectedCount: number;
    selectedMw: number;
  };
};

@Component({
  standalone: true,
  selector: 'app-bess-place-bid-page',
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './bess-place-bid.page.html',
  styleUrl: './bess-place-bid.page.scss',
})
export class BessPlaceBidPage {
  private readonly batteryService = inject(BatteryConfigurationsService);
  private readonly operationsService = inject(BessOperationsService);
  private readonly toast = inject(ToastService);

  protected readonly BatteryChargingIcon = BatteryCharging;
  protected readonly CalendarRangeIcon = CalendarRange;
  protected readonly CheckCircle2Icon = CheckCircle2;
  protected readonly RefreshCcwIcon = RefreshCcw;

  protected readonly loading = signal(false);
  protected readonly placing = signal(false);
  protected readonly syncing = signal(false);
  protected readonly assets = signal<BatteryConfiguration[]>([]);
  protected readonly schedules = signal<BessSchedule[]>([]);
  protected readonly forecasts = signal<BessForecastResponse | null>(null);
  protected readonly forecastPriceByBlock = signal<Record<number, number>>({});
  protected readonly product = signal<BidProduct>('DAM');
  protected readonly selectedDate = signal(this.defaultDate('DAM'));
  protected readonly plantSearch = signal('');
  protected readonly suggestionsByAsset = signal<Record<string, BidSuggestion[]>>({});
  protected readonly expandedAssetId = signal<string>('');

  protected readonly currentBlock = computed(() => this.resolveCurrentBlock());
  protected readonly currentBlockLabel = computed(() => this.blockLabel(this.currentBlock()));
  protected readonly minAllowedDate = computed(() => this.defaultDate(this.product()));
  protected readonly eligibleStartBlock = computed(() => this.forecasts()?.forecast_start_block || (this.product() === 'RTM' ? Math.min(96, this.currentBlock() + 4) : 1));
  protected readonly eligibleDateLabel = computed(() =>
    this.product() === 'DAM'
      ? `Tomorrow only: ${this.selectedDate()}`
      : `Today from block ${this.eligibleStartBlock()} onward: ${this.selectedDate()}`,
  );
  protected readonly plantPanels = computed<PlantBidPanel[]>(() =>
    this.assets().map((asset) => {
      const suggestions = this.suggestionsByAsset()[asset.id] || [];
      const selected = suggestions.filter((item) => item.selected && item.eligible && item.action !== 'hold');
      return {
        asset,
        suggestions,
        summary: {
          totalEligible: suggestions.filter((item) => item.eligible).length,
          chargeCount: suggestions.filter((item) => item.eligible && item.action === 'charge').length,
          dischargeCount: suggestions.filter((item) => item.eligible && item.action === 'discharge').length,
          selectedCount: selected.length,
          selectedMw: selected.reduce((sum, item) => sum + item.bidMw, 0),
        },
      };
    }),
  );
  protected readonly filteredPlantPanels = computed<PlantBidPanel[]>(() => {
    const term = this.plantSearch().trim().toLowerCase();
    if (!term) {
      return this.plantPanels();
    }
    return this.plantPanels().filter((panel) => {
      const haystack = [
        panel.asset.entry_id,
        panel.asset.plant_type,
        String(panel.asset.power_capacity_mw),
        String(panel.asset.energy_capacity_mw),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  });
  protected readonly overallSummary = computed(() => ({
    totalEligible: this.plantPanels().reduce((sum, panel) => sum + panel.summary.totalEligible, 0),
    chargeCount: this.plantPanels().reduce((sum, panel) => sum + panel.summary.chargeCount, 0),
    dischargeCount: this.plantPanels().reduce((sum, panel) => sum + panel.summary.dischargeCount, 0),
    selectedMw: this.plantPanels().reduce((sum, panel) => sum + panel.summary.selectedMw, 0),
  }));

  constructor() {
    this.load();
  }

  protected setProduct(value: string): void {
    const nextProduct: BidProduct = value === 'RTM' ? 'RTM' : 'DAM';
    this.product.set(nextProduct);
    this.selectedDate.set(this.defaultDate(nextProduct));
    this.loadSuggestions();
  }

  protected setDate(value: string): void {
    const next = value || this.defaultDate(this.product());
    this.selectedDate.set(next < this.minAllowedDate() ? this.minAllowedDate() : next);
    this.loadSuggestions();
  }

  protected setPlantSearch(value: string): void {
    this.plantSearch.set(value || '');
    const firstMatch = this.filteredPlantPanels()[0];
    if (firstMatch) {
      this.expandedAssetId.set(firstMatch.asset.id);
    }
  }

  protected togglePanel(assetId: string): void {
    this.expandedAssetId.update((current) => (current === assetId ? '' : assetId));
  }

  protected isExpanded(assetId: string): boolean {
    return this.expandedAssetId() === assetId;
  }

  protected toggleSuggestion(assetId: string, block: number, selected: boolean): void {
    this.suggestionsByAsset.update((state) => ({
      ...state,
      [assetId]: (state[assetId] || []).map((item) =>
        item.block === block && item.eligible && item.action !== 'hold' && !item.existingScheduleId
          ? { ...item, selected }
          : item,
      ),
    }));
  }

  protected selectAllEligible(assetId: string, selected: boolean): void {
    this.suggestionsByAsset.update((state) => ({
      ...state,
      [assetId]: (state[assetId] || []).map((item) =>
        item.eligible && item.action !== 'hold' && !item.existingScheduleId
          ? { ...item, selected }
          : item,
      ),
    }));
  }

  protected refreshSuggestions(): void {
    this.loadSuggestions(true);
  }

  protected placeSelectedBids(assetId: string): void {
    const asset = this.assets().find((item) => item.id === assetId) || null;
    const items = (this.suggestionsByAsset()[assetId] || []).filter((item) => item.selected && item.eligible && item.action !== 'hold' && !item.existingScheduleId);
    if (!asset || items.length === 0) {
      this.toast.show('Select at least one suggested block for the plant', '', 'info');
      return;
    }

    this.placing.set(true);
    forkJoin(
      items.map((item) =>
        this.operationsService.createSchedule({
          battery_configuration_id: asset.id,
          schedule_date: this.selectedDate(),
          time_block: item.block,
          action: item.action,
          energy_mw: item.bidMw,
          market_price: item.marketPrice,
          status: 'submitted',
        }),
      ),
    ).subscribe({
      next: () => {
        this.placing.set(false);
        this.toast.show(`${items.length} bid blocks placed for ${asset.entry_id}`, '', 'success');
        this.load();
      },
      error: () => {
        this.placing.set(false);
        this.toast.show('Failed to place selected bids', '', 'danger');
      },
    });
  }

  protected rowState(item: BidSuggestion): string {
    if (!item.eligible) return 'Not eligible';
    if (item.existingScheduleId) return item.existingStatus || 'Already placed';
    if (item.action === 'hold') return 'Watch';
    return item.selected ? 'Ready' : 'Suggested';
  }

  private load(): void {
    this.loading.set(true);
    forkJoin({
      assets: this.batteryService.list(),
      schedules: this.operationsService.listSchedules(),
    }).subscribe({
      next: ({ assets, schedules }) => {
        this.assets.set(assets || []);
        this.schedules.set(schedules || []);
        if (!this.expandedAssetId() && assets?.length) {
          this.expandedAssetId.set(assets[0].id);
        }
        this.loadSuggestions();
      },
      error: () => {
        this.loading.set(false);
        this.toast.show('Failed to load BESS bid placement data', '', 'danger');
      },
    });
  }

  private loadSuggestions(forceRefetch = false): void {
    this.loading.set(true);
    const product = this.product();
    const date = this.selectedDate();
    this.syncing.set(true);

    const fetchForecasts = () =>
      forkJoin({
        forecasts: this.operationsService.listForecasts(product, date),
        snapshotId: this.operationsService.listForecastPrices(product, date).pipe(
          map((snapshots) => this.pickMatchingForecastPrice(snapshots, date)?.id || null),
          catchError(() => of(null)),
        ),
      }).subscribe({
        next: ({ forecasts, snapshotId }) => {
          const applyForecasts = (priceMap: Record<number, number>) => {
            this.forecastPriceByBlock.set(priceMap);
            this.forecasts.set(forecasts);
            if (forecasts.forecast_date) {
              this.selectedDate.set(forecasts.forecast_date);
            }
            this.rebuildSuggestions();
            this.loading.set(false);
            this.syncing.set(false);
          };

          if (!snapshotId) {
            applyForecasts({});
            return;
          }

          this.operationsService.getForecastPrice(snapshotId).pipe(
            map((detail) => this.buildForecastPriceMap(detail)),
            catchError(() => of({} as Record<number, number>)),
          ).subscribe({
            next: (priceMap) => applyForecasts(priceMap),
            error: () => applyForecasts({}),
          });
        },
        error: () => {
          this.forecasts.set(null);
          this.forecastPriceByBlock.set({});
          this.suggestionsByAsset.set({});
          this.loading.set(false);
          this.syncing.set(false);
          this.toast.show('Failed to load BESS forecasts for bid suggestions', '', 'danger');
        },
      });

    this.operationsService.autoFetchForecastPrice(product, date, forceRefetch).subscribe({
      next: () => fetchForecasts(),
      error: () => fetchForecasts(),
    });
  }

  private rebuildSuggestions(): void {
    const forecastAssets = new Map<string, BessForecastAsset>(
      (this.forecasts()?.products?.[this.product()] || []).map((asset) => [asset.battery_configuration_id, asset]),
    );
    if (!this.forecasts()) {
      this.suggestionsByAsset.set({});
      return;
    }

    const nextState: Record<string, BidSuggestion[]> = {};
    for (const asset of this.assets()) {
      const schedulesByBlock = new Map<number, BessSchedule>(
        this.schedules()
          .filter((row) => row.battery_configuration_id === asset.id && row.schedule_date === this.selectedDate())
          .map((row) => [row.time_block, row]),
      );
      const forecastAsset = forecastAssets.get(asset.id);
      const forecastBlocks = forecastAsset?.blocks || [];
      nextState[asset.id] = forecastBlocks.map((block) => this.buildSuggestion(block, asset, schedulesByBlock));
    }
    this.suggestionsByAsset.set(nextState);
  }

  private buildSuggestion(
    block: BessForecastBlock,
    asset: BatteryConfiguration,
    schedulesByBlock: Map<number, BessSchedule>,
  ): BidSuggestion {
    const snapshotPrice = this.forecastPriceByBlock()[block.block_number];
    const marketPrice = Number.isFinite(snapshotPrice) ? snapshotPrice : this.toNumber(block.forecast_price ?? block.market_price);
    const eligible = this.isEligibleBlock(block.block_number, this.eligibleStartBlock());
    const existing = schedulesByBlock.get(block.block_number) || null;
    const forecastAction = this.normalizeForecastAction(block.action);
    const action: BidSuggestion['action'] = eligible ? forecastAction : 'hold';
    const bidMw = action === 'hold' ? 0 : this.toNumber(block.forecast_mw) || (action === 'charge'
      ? this.toNumber(asset.charging_power_mw) || this.toNumber(asset.power_capacity_mw)
      : this.toNumber(asset.discharging_power_mw) || this.toNumber(asset.power_capacity_mw));
    const reason = eligible
      ? block.rationale || 'AI forecast suggestion'
      : this.product() === 'DAM'
        ? 'Only tomorrow bids are allowed'
        : `RTM opens from block ${this.eligibleStartBlock()}`;

    return {
      block: block.block_number,
      blockLabel: block.block_label,
      marketPrice,
      action,
      bidMw: Number(bidMw.toFixed(2)),
      reason,
      eligible,
      selected: eligible && action !== 'hold' && !existing,
      existingScheduleId: existing?.id || null,
      existingStatus: existing?.status || null,
    };
  }

  private pickMatchingForecastPrice(snapshots: ForecastPriceSnapshot[], selectedDate: string): ForecastPriceSnapshot | null {
    return snapshots.find((snapshot) => snapshot.forecast_date === selectedDate) || snapshots[0] || null;
  }

  private buildForecastPriceMap(detail: ForecastPriceSnapshotDetail | null): Record<number, number> {
    const next: Record<number, number> = {};
    for (const row of detail?.blocks || []) {
      const blockNumber = Number(row.block_number);
      const value = this.toNumber(row.market_price);
      if (Number.isFinite(blockNumber) && blockNumber >= 1 && blockNumber <= 96 && Number.isFinite(value)) {
        next[blockNumber] = value;
      }
    }
    return next;
  }

  private isEligibleBlock(block: number, eligibleStartBlock: number): boolean {
    if (this.product() === 'DAM') {
      return true;
    }
    return block >= eligibleStartBlock;
  }

  private resolveCurrentBlock(): number {
    const now = new Date();
    const minutes = now.getHours() * 60 + now.getMinutes();
    return Math.min(96, Math.max(1, Math.floor(minutes / 15) + 1));
  }

  private blockLabel(block: number): string {
    const minutes = (block - 1) * 15;
    const startHour = Math.floor(minutes / 60);
    const startMinute = minutes % 60;
    const endMinutes = minutes + 15;
    const endHour = Math.floor(endMinutes / 60) % 24;
    const endMinute = endMinutes % 60;
    return `${String(startHour).padStart(2, '0')}:${String(startMinute).padStart(2, '0')}-${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`;
  }

  private defaultDate(product: BidProduct): string {
    const date = new Date();
    if (product === 'DAM') {
      date.setDate(date.getDate() + 1);
    }
    return date.toISOString().slice(0, 10);
  }

  private toNumber(value: string | number | null | undefined): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private normalizeForecastAction(action: string | null | undefined): BidSuggestion['action'] {
    const normalized = (action || '').toLowerCase();
    if (normalized === 'charge') return 'charge';
    if (normalized === 'discharge') return 'discharge';
    return 'hold';
  }
}
